import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Eye, Heart, MessageCircle, Phone, Share2, Calendar, Building2, UserPlus, Trophy, AlertTriangle, Clock } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const KIND_ICON = {
  property_view: Eye,
  property_favorite: Heart,
  property_share: Share2,
  contact_whatsapp: MessageCircle,
  contact_phone: Phone,
  message: MessageCircle,
  visit_request: Calendar,
  profile_view: Eye,
  agent_whatsapp: MessageCircle,
  lead: Building2,
  demo: Building2,
  // Iter 39 · FASE 4B.1 — CRM/Integrations notifications
  'lead.new': UserPlus,
  'lead.replied': MessageCircle,
  'visit.today': Calendar,
  'task.due': AlertTriangle,
  'lead.stale': Clock,
  'deal.won': Trophy,
  'integration.ok': Bell,
  'integration.error': AlertTriangle,
  system: Bell,
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString('es-CO');
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const d = await api.getNotifications();
      setData(d);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Refresh count every 60s
  useEffect(() => {
    if (!user) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try { await api.markNotificationRead(n.id); } catch { /* silent */ }
    }
    setOpen(false);
  };

  const onMarkAll = async () => {
    try { await api.markAllNotificationsRead(); await load(); } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) load(); }}
        className="relative w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
        aria-label="Notificaciones"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-habita-dark" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
            {data.unread > 9 ? '9+' : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden z-50" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="font-serif font-bold text-habita-dark">Notificaciones</p>
            {data.unread > 0 && (
              <button onClick={onMarkAll} className="text-xs text-habita hover:underline" data-testid="notifications-mark-all">
                Marcar todo leído
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && <div className="px-4 py-8 text-center text-sm text-gray-500">Cargando…</div>}
            {!loading && data.items.length === 0 && (
              <div className="px-4 py-12 text-center" data-testid="notifications-empty">
                <Bell className="w-8 h-8 mx-auto text-gray-300" />
                <p className="text-sm text-gray-500 mt-3">Sin notificaciones por ahora.</p>
                <p className="text-xs text-gray-400 mt-1">Aquí verás visitas, contactos y leads cuando ocurran.</p>
              </div>
            )}
            {data.items.map(n => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <Link
                  key={n.id}
                  to={n.link || '#'}
                  onClick={() => onItemClick(n)}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-l-2 ${n.read_at ? 'border-transparent' : 'border-habita bg-habita-cream/30'}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-habita-mint flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-habita" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-habita-dark leading-snug">
                      {n.title}
                      {n.count > 1 && <span className="ml-1 text-xs font-semibold text-habita">×{n.count}</span>}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{timeAgo(n.updated_at || n.created_at)}</p>
                  </div>
                  {!n.read_at && <span className="w-2 h-2 rounded-full bg-habita mt-2 shrink-0" />}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
